import React from 'react';
import { motion } from 'framer-motion';
import * as LucideIcons from 'lucide-react';

interface ProfessionalServicesProps {
  setCurrentPage: (page: string) => void;
}

const ProfessionalServices: React.FC<ProfessionalServicesProps> = ({ setCurrentPage }) => {
  const features = [
    {
      icon: LucideIcons.Target,
      title: "Strategic Consulting",
      description: "Expert guidance on security strategy, architecture, and roadmap development."
    },
    {
      icon: LucideIcons.Shield,
      title: "Security Assessment",
      description: "Comprehensive evaluation of security posture and risk assessment."
    },
    {
      icon: LucideIcons.Cog,
      title: "Implementation Services",
      description: "Professional deployment and integration of security solutions."
    },
    {
      icon: LucideIcons.Brain,
      title: "Expert Advisory",
      description: "Specialized security expertise and continuous guidance."
    }
  ];

  const services = [
    {
      title: "Security Strategy",
      items: [
        "Security roadmap development",
        "Architecture design",
        "Risk management",
        "Compliance planning"
      ]
    },
    {
      title: "Technical Services",
      items: [
        "Security implementation",
        "System integration",
        "Performance optimization",
        "Infrastructure security"
      ]
    },
    {
      title: "Advisory Services",
      items: [
        "Expert consultation",
        "Best practices guidance",
        "Technology selection",
        "Security optimization"
      ]
    }
  ];

  const solutions = [
    {
      icon: LucideIcons.Laptop,
      title: "Digital Security",
      description: "End-to-end digital security solutions for modern enterprises.",
      features: [
        "Endpoint protection",
        "Network security",
        "Application security",
        "Data protection"
      ]
    },
    {
      icon: LucideIcons.Network,
      title: "Infrastructure Security",
      description: "Comprehensive infrastructure security solutions.",
      features: [
        "Network architecture",
        "Security controls",
        "Access management",
        "Monitoring systems"
      ]
    },
    {
      icon: LucideIcons.Database,
      title: "Data Security",
      description: "Advanced data security and protection services.",
      features: [
        "Data encryption",
        "Access controls",
        "Data governance",
        "Compliance management"
      ]
    },
    {
      icon: LucideIcons.Cloud,
      title: "Cloud Security",
      description: "Cloud-native security solutions and services.",
      features: [
        "Cloud architecture",
        "Security controls",
        "Compliance framework",
        "Performance optimization"
      ]
    }
  ];

  const expertise = [
    {
      icon: LucideIcons.FileCheck,
      title: "Industry Standards",
      items: [
        "ISO 27001 compliance",
        "NIST framework",
        "PCI DSS standards",
        "HIPAA compliance"
      ]
    },
    {
      icon: LucideIcons.Users,
      title: "Expert Team",
      items: [
        "Certified professionals",
        "Industry specialists",
        "Technical experts",
        "Security architects"
      ]
    },
    {
      icon: LucideIcons.LineChart,
      title: "Proven Results",
      items: [
        "Measurable outcomes",
        "Performance metrics",
        "Success tracking",
        "Continuous improvement"
      ]
    }
  ];

  return (
    <div className="min-h-screen hero-gradient pt-32 pb-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Hero Section */}
        <div className="text-center mb-20">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="inline-block mb-6"
          >
            <LucideIcons.Briefcase className="h-16 w-16 text-primary animate-glow" />
          </motion.div>
          
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-4xl md:text-5xl font-bold mb-6"
          >
            <span className="text-gradient">Professional IT Services</span>
          </motion.h1>
          
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-lg text-muted max-w-3xl mx-auto"
          >
            Expert security consulting and implementation services to strengthen your organization's security posture
          </motion.p>
        </div>

        {/* Features Grid */}
        <div className="mb-20">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              <span className="text-gradient">Professional Services Features</span>
            </h2>
            <p className="text-lg text-muted">
              Comprehensive security consulting and implementation services
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => {
              const IconComponent = feature.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 + index * 0.1 }}
                  className="glass-panel rounded-xl p-6 hover-card"
                >
                  <div className="flex items-center justify-center w-14 h-14 bg-gradient-to-br from-primary to-accent rounded-lg mb-6">
                    <IconComponent className="h-7 w-7 text-white" />
                  </div>
                  <h3 className="text-xl font-semibold mb-3">
                    {feature.title}
                  </h3>
                  <p className="text-muted">
                    {feature.description}
                  </p>
                </motion.div>
              );
            })}
          </div>
        </div>

        {/* Services Section */}
        <div className="mb-20">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              <span className="text-gradient">Our Services</span>
            </h2>
            <p className="text-lg text-muted">
              Comprehensive professional services portfolio
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 + index * 0.1 }}
                className="glass-panel rounded-xl p-8 hover-card"
              >
                <h3 className="text-2xl font-semibold mb-6">
                  {service.title}
                </h3>
                <ul className="space-y-4">
                  {service.items.map((item, itemIndex) => (
                    <li key={itemIndex} className="flex items-center">
                      <LucideIcons.CheckCircle className="h-5 w-5 text-primary mr-3" />
                      <span className="text-muted">{item}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Solutions Grid */}
        <div className="mb-20">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              <span className="text-gradient">Security Solutions</span>
            </h2>
            <p className="text-lg text-muted">
              Comprehensive security solutions for modern enterprises
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {solutions.map((solution, index) => {
              const IconComponent = solution.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5 + index * 0.1 }}
                  className="glass-panel rounded-xl p-8 hover-card"
                >
                  <div className="flex items-center mb-6">
                    <div className="flex items-center justify-center w-14 h-14 bg-gradient-to-br from-primary to-accent rounded-lg">
                      <IconComponent className="h-7 w-7 text-white" />
                    </div>
                    <h3 className="text-2xl font-semibold ml-4">
                      {solution.title}
                    </h3>
                  </div>
                  <p className="text-muted mb-6">
                    {solution.description}
                  </p>
                  <ul className="space-y-3">
                    {solution.features.map((feature, featureIndex) => (
                      <li key={featureIndex} className="flex items-center">
                        <LucideIcons.CheckCircle className="h-5 w-5 text-primary mr-3" />
                        <span className="text-muted">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </motion.div>
              );
            })}
          </div>
        </div>

        {/* Expertise Section */}
        <div className="mb-20">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              <span className="text-gradient">Our Expertise</span>
            </h2>
            <p className="text-lg text-muted">
              Industry-leading expertise and proven track record
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {expertise.map((expert, index) => {
              const IconComponent = expert.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.6 + index * 0.1 }}
                  className="glass-panel rounded-xl p-8 hover-card"
                >
                  <div className="flex items-center mb-6">
                    <div className="flex items-center justify-center w-14 h-14 bg-gradient-to-br from-primary to-accent rounded-lg">
                      <IconComponent className="h-7 w-7 text-white" />
                    </div>
                    <h3 className="text-2xl font-semibold ml-4">
                      {expert.title}
                    </h3>
                  </div>
                  <ul className="space-y-4">
                    {expert.items.map((item, itemIndex) => (
                      <li key={itemIndex} className="flex items-center">
                        <LucideIcons.CheckCircle className="h-5 w-5 text-primary mr-3" />
                        <span className="text-muted">{item}</span>
                      </li>
                    ))}
                  </ul>
                </motion.div>
              );
            })}
          </div>
        </div>

        {/* CTA Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7 }}
          className="text-center"
        >
          <div className="glass-panel rounded-2xl p-8 md:p-12">
            <div className="max-w-3xl mx-auto text-center">
              <h2 className="text-3xl font-bold mb-6">
                Ready to Transform Your Security?
              </h2>
              <p className="text-lg text-muted mb-8 max-w-2xl mx-auto">
                Get started with our professional services today
              </p>
              <button 
                onClick={() => setCurrentPage('get-started')}
                className="btn-primary"
              >
                <span className="flex items-center justify-center">
                  Request Consultation
                  <LucideIcons.ArrowRight className="ml-2 h-5 w-5" />
                </span>
              </button>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default ProfessionalServices;